import { Link, createFileRoute } from '@tanstack/react-router';
import { useSettings } from '@/shared/hooks/useSettings';

function HomePage() {
  const [settings] = useSettings();
  const hasToken = settings.replicateToken.length > 0;

  return (
    <div className="relative -mx-4 -my-8 overflow-hidden">
      {/* Atmospheric background */}
      <Backdrop />

      <div className="relative mx-auto max-w-6xl px-6 pb-32 pt-20 sm:pt-28">
        {/* ─────────── Hero ─────────── */}
        <section className="relative grid items-end gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-accent opacity-60 animate-pulse-soft" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent" />
              </span>
              Free · Browser-only · Bring your own model
            </div>

            <h1 className="mt-6 text-balance text-[2.9rem] font-semibold leading-[0.98] tracking-tight sm:text-[4.5rem]">
              Static ad creatives,
              <br />
              <span className="font-serif font-normal italic text-foreground/95">
                fluent in your{' '}
              </span>
              <span className="relative inline-block font-serif font-normal italic">
                brand
                <svg
                  aria-hidden
                  viewBox="0 0 200 12"
                  className="absolute -bottom-1 left-0 h-2.5 w-full text-accent"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M2 8 Q 50 2, 100 6 T 198 4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              .
            </h1>

            <p className="mt-8 max-w-xl text-balance text-lg leading-relaxed text-muted-foreground">
              Drop a brand name and URL, plus a few product shots if you have them. Get a research
              dossier, thirteen funnel-aware ad prompts, and a gallery of static creatives,
              rendered by the model of your choice. All in your browser.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-4">
              <Link
                to={hasToken ? '/brands/new' : '/settings'}
                className="group relative inline-flex h-12 items-center overflow-hidden rounded-full bg-foreground pl-6 pr-5 text-sm font-medium text-background transition-transform hover:-translate-y-0.5"
              >
                <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-accent/40 via-accent/0 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                <span className="relative">{hasToken ? 'Start a new brand' : 'Add your token'}</span>
                <span
                  aria-hidden
                  className="relative ml-3 flex h-7 w-7 items-center justify-center rounded-full bg-background/15 transition-transform group-hover:translate-x-0.5"
                >
                  →
                </span>
              </Link>
              <span className="text-sm text-muted-foreground">
                {hasToken ? (
                  <>
                    Token saved.{' '}
                    <Link to="/brands" className="text-foreground underline-offset-4 hover:underline">
                      Browse brands →
                    </Link>
                  </>
                ) : (
                  <>One Replicate token, stored only on this device.</>
                )}
              </span>
            </div>

            {/* Trust line */}
            <dl className="mt-14 grid max-w-lg grid-cols-3 gap-6 border-t border-border/60 pt-6">
              <Trust k="13" v="ad templates" />
              <Trust k="5" v="image models" />
              <Trust k="0" v="servers, ever" />
            </dl>
          </div>

          {/* Floating preview cluster */}
          <div className="relative hidden lg:col-span-5 lg:block">
            <FloatingPreview />
          </div>
        </section>

        {/* Mobile/tablet preview marquee */}
        <section className="mt-16 lg:hidden">
          <SampleMarquee />
        </section>

        {/* ─────────── Gallery ─────────── */}
        <section className="mt-32">
          <SectionLabel
            kicker="One brand, thirteen renders"
            title="What it actually ships."
            sub="Every image below is from a single run on a fictional Ford Model T relaunch. One DNA dossier, thirteen funnel-aware templates, your model of choice. No cherry-picking, no retouching."
          />
          <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            <SampleHeadline />
            <SampleFamily />
            <SampleBeforeAfter />
            <SampleStat />
            <SampleFounder />
            <SampleTestimonial />
            <SamplePromo />
            <SampleUgc />
          </div>
        </section>

        {/* ─────────── How it works ─────────── */}
        <section className="mt-32">
          <SectionLabel kicker="How it works" title="Four steps. Editable at every one." />

          <div className="relative mt-14 grid gap-px overflow-hidden rounded-2xl border border-border bg-border md:grid-cols-2 lg:grid-cols-4">
            <Step
              n="01"
              title="Research"
              body="An LLM reads the brand's homepage and writes a structured DNA: colors, typography, audience, voice, photography mood, product type."
              accent="Claude / GPT"
            />
            <Step
              n="02"
              title="Upload"
              body="Optionally drop in a few clean product shots or app screenshots. Edit-capable models lock onto them so the product looks like itself, not a hallucination."
              accent="Optional"
            />
            <Step
              n="03"
              title="Prompts"
              body="Your DNA is poured into thirteen funnel-aware templates and produced as model-ready prompts you can review and edit before any image renders."
              accent="Editable"
            />
            <Step
              n="04"
              title="Images"
              body="Each prompt is rendered by your chosen image model. Jobs run in the background and results land in a per-brand gallery you can revisit, export, or rerun."
              accent="On-device"
            />
          </div>

          {/* Models row */}
          <div className="mt-12 grid gap-8 md:grid-cols-2">
            <ModelGroup
              kicker="Pick your writer"
              models={['Claude Sonnet 4.5', 'Claude Opus 4.7', 'GPT-5.4']}
            />
            <ModelGroup
              kicker="Pick your renderer"
              models={['Nano Banana 2', 'Flux 1.1 Pro', 'Ideogram v3', 'Recraft v3', 'Seedream 4']}
            />
          </div>

          <p className="mt-10 max-w-2xl text-[15px] leading-relaxed text-muted-foreground">
            Cost is estimated up front and reconciled against Replicate&apos;s reported usage
            after each run, so you always know what a brand costs to generate. Templates that
            need a real product reference automatically narrow the model picker to ones that
            accept image inputs.
          </p>
        </section>

        {/* ─────────── Privacy block ─────────── */}
        <section className="mt-32">
          <SectionLabel
            kicker="Privacy"
            title="Your work stays on this device."
            sub="No accounts. No telemetry on your brands. Clear your storage and the data is gone. There is no backend that could retain it."
          />

          <div className="mt-12 overflow-hidden rounded-2xl border border-border bg-muted/30 shadow-2xl shadow-black/20">
            <div className="flex items-center gap-2 border-b border-border bg-background/40 px-4 py-2.5">
              <div className="flex gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
                <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
                <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
              </div>
              <span className="ml-2 font-mono text-[11px] text-muted-foreground">
                /etc/brand-generator/runtime.conf
              </span>
            </div>
            <div className="grid gap-y-2 p-6 font-mono text-[13px] leading-7 sm:grid-cols-2 sm:gap-x-10">
              <ConfigRow k="storage" v="localStorage + IndexedDB" />
              <ConfigRow k="network" v="api.replicate.com only" />
              <ConfigRow k="auth" v="your token, on-device" />
              <ConfigRow k="accounts" v="none" />
              <ConfigRow k="server" v="none, static SPA" />
              <ConfigRow k="analytics" v="none on your work" />
            </div>
          </div>
        </section>

        {/* ─────────── CTA ─────────── */}
        <section className="mt-32 overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-muted/40 via-background to-background p-10 sm:p-14">
          <div className="flex flex-col items-start gap-8 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="max-w-md text-balance text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">
                Ready to make ads that{' '}
                <span className="font-serif italic font-normal">sound like you</span>?
              </h3>
              <p className="mt-3 max-w-md text-muted-foreground">
                One token. Thirteen templates. Endless brands.
              </p>
            </div>
            <Link
              to={hasToken ? '/brands/new' : '/settings'}
              className="group inline-flex h-12 items-center rounded-full bg-foreground px-6 text-sm font-medium text-background transition-transform hover:-translate-y-0.5"
            >
              {hasToken ? 'Generate a brand' : 'Configure token'}
              <span aria-hidden className="ml-2 transition-transform group-hover:translate-x-0.5">
                →
              </span>
            </Link>
          </div>
        </section>

      </div>
    </div>
  );
}

/* ─────────── Backdrop ─────────── */

function Backdrop() {
  return (
    <>
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[720px] bg-grid mask-fade-b opacity-60"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 left-1/2 -z-10 h-[600px] w-[900px] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,_hsl(var(--glow)/0.22),_transparent_65%)] blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -top-10 right-0 -z-10 h-[420px] w-[520px] rounded-full bg-[radial-gradient(ellipse_at_center,_hsl(220_90%_55%/0.18),_transparent_65%)] blur-3xl"
      />
    </>
  );
}

/* ─────────── Small components ─────────── */

function SectionLabel({
  kicker,
  title,
  sub,
}: {
  kicker: string;
  title: string;
  sub?: string;
}) {
  return (
    <div className="max-w-2xl">
      <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
        {kicker}
      </p>
      <h2 className="mt-4 text-balance text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">
        {title}
      </h2>
      {sub ? (
        <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-muted-foreground">{sub}</p>
      ) : null}
    </div>
  );
}

function Trust({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <dt className="font-serif text-3xl font-normal text-foreground">{k}</dt>
      <dd className="mt-1 text-xs uppercase tracking-widest text-muted-foreground">{v}</dd>
    </div>
  );
}

function Step({
  n,
  title,
  body,
  accent,
}: {
  n: string;
  title: string;
  body: string;
  accent: string;
}) {
  return (
    <div className="relative bg-background p-8 transition-colors hover:bg-muted/30">
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-xs text-muted-foreground">{n}</span>
        <span className="rounded-full border border-border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {accent}
        </span>
      </div>
      <h3 className="mt-6 font-serif text-2xl font-normal">{title}</h3>
      <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}

function ModelGroup({ kicker, models }: { kicker: string; models: string[] }) {
  return (
    <div>
      <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
        {kicker}
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        {models.map((m) => (
          <span
            key={m}
            className="rounded-full border border-border bg-background/60 px-3 py-1.5 text-[13px] text-foreground/90 backdrop-blur-sm transition-colors hover:border-accent/50 hover:text-foreground"
          >
            {m}
          </span>
        ))}
      </div>
    </div>
  );
}

function ConfigRow({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex flex-wrap gap-x-4">
      <span className="w-24 shrink-0 text-muted-foreground">
        <span className="text-accent">›</span> {k}
      </span>
      <span className="text-foreground">{v}</span>
    </div>
  );
}

/* ─────────── Floating hero preview ─────────── */

function FloatingPreview() {
  return (
    <div className="relative h-[540px]">
      <div className="absolute right-0 top-2 w-[72%] rotate-[3deg]">
        <div className="animate-float">
          <SampleHeadline />
        </div>
      </div>
      <div className="absolute left-0 top-40 w-[60%] -rotate-[5deg]">
        <div className="animate-float" style={{ animationDelay: '0.8s' }}>
          <SampleFamily />
        </div>
      </div>
      <div className="absolute right-4 bottom-0 w-[58%] rotate-[2deg]">
        <div className="animate-float" style={{ animationDelay: '1.6s' }}>
          <SampleStat />
        </div>
      </div>
    </div>
  );
}

/* ─────────── Sample marquee (mobile) ─────────── */

function SampleMarquee() {
  const items = [
    <SampleHeadline key="h" />,
    <SampleFamily key="f" />,
    <SampleStat key="s" />,
    <SampleFounder key="m" />,
    <SampleTestimonial key="t" />,
    <SamplePromo key="p" />,
  ];
  return (
    <div className="mask-fade-x">
      <div className="flex w-max animate-marquee gap-4">
        {[...items, ...items, ...items].map((item, i) => (
          <div key={i} className="w-[180px] shrink-0">
            {item}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────── Sample creatives (pure CSS) ─────────── */

function SampleHeadline() {
  return <ImageFrame label="Hero · 4:5" src="/samples/ford-hero.jpeg" aspect="aspect-[4/5]" />;
}

function SampleStat() {
  return <ImageFrame label="Stat · 4:5" src="/samples/ford-stat.jpeg" aspect="aspect-[4/5]" />;
}

function SampleFounder() {
  return (
    <ImageFrame label="Founder · 4:5" src="/samples/ford-founder.jpeg" aspect="aspect-[4/5]" />
  );
}

function SampleTestimonial() {
  return (
    <ImageFrame
      label="Testimonial · 4:5"
      src="/samples/ford-testimonial.jpeg"
      aspect="aspect-[4/5]"
    />
  );
}

function SampleBeforeAfter() {
  return (
    <ImageFrame
      label="Before / After · 4:5"
      src="/samples/ford-before-after.jpeg"
      aspect="aspect-[4/5]"
    />
  );
}

function SamplePromo() {
  return <ImageFrame label="Promo · 9:16" src="/samples/ford-promo.jpeg" aspect="aspect-[9/16]" />;
}

function SampleFamily() {
  return (
    <ImageFrame label="Manifesto · 9:16" src="/samples/ford-family.jpeg" aspect="aspect-[9/16]" />
  );
}

function SampleUgc() {
  return <ImageFrame label="UGC · 9:16" src="/samples/ford-ugc.jpeg" aspect="aspect-[9/16]" />;
}

function ImageFrame({
  label,
  src,
  aspect,
}: {
  label: string;
  src: string;
  aspect: string;
}) {
  return (
    <figure className="group space-y-2">
      <div
        className={`overflow-hidden rounded-lg border border-border bg-muted shadow-lg shadow-black/20 transition-transform duration-300 group-hover:-translate-y-1 group-hover:shadow-xl group-hover:shadow-black/30 ${aspect}`}
      >
        <img
          src={src}
          alt={label}
          loading="lazy"
          className="h-full w-full object-cover"
        />
      </div>
      <figcaption className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        {label}
      </figcaption>
    </figure>
  );
}

export const Route = createFileRoute('/')({
  component: HomePage,
});
